import { useMemo } from 'react';
import { Button, Card, Space, Tree, Typography, type TreeDataNode } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { listCategories } from './api';
import { categoryDisplayName, type CategoryView } from './types';

function buildTree(rows: CategoryView[]): TreeDataNode[] {
  const nodes = new Map<number, TreeDataNode & { _children: TreeDataNode[] }>();
  for (const row of rows) {
    nodes.set(row.id, {
      key: row.id,
      title: `${categoryDisplayName(row)}  (#${row.id} · 排序 ${row.sortOrder ?? 0})`,
      _children: [],
      children: [],
    });
  }
  const roots: TreeDataNode[] = [];
  for (const row of rows) {
    const node = nodes.get(row.id);
    if (!node) continue;
    node.children = node._children;
    if (row.parentId && nodes.has(row.parentId)) {
      nodes.get(row.parentId)!._children.push(node);
    } else {
      roots.push(node);
    }
  }
  return roots;
}

export function CategoriesPage() {
  const query = useQuery({ queryKey: ['admin', 'categories'], queryFn: listCategories });
  const tree = useMemo(() => buildTree(query.data ?? []), [query.data]);

  return (
    <div className="mall-page">
      <div className="mall-page-header">
        <div>
          <div className="mall-page-kicker">Categories</div>
          <Typography.Title level={3} className="mall-page-title">
            分类
          </Typography.Title>
          <p className="mall-page-description">当前商户有商品的分类树，按全局分类结构只读展示。</p>
        </div>
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => query.refetch()}
            loading={query.isFetching}
          >
            刷新
          </Button>
        </Space>
      </div>
      <Card loading={query.isLoading} className="mall-surface">
        <div className="mall-table-toolbar" style={{ margin: '-18px -18px 14px' }}>
          <div>
            <div className="mall-table-title">分类树</div>
            <div className="mall-table-subtitle">共 {query.data?.length ?? 0} 个可见分类</div>
          </div>
        </div>
        {tree.length === 0 && !query.isLoading && (
          <Typography.Text type="secondary">暂无可用分类</Typography.Text>
        )}
        {tree.length > 0 && (
          <Tree
            className="mall-tree-list"
            treeData={tree}
            defaultExpandAll
            showLine
            blockNode
            selectable={false}
            style={{ background: 'transparent' }}
          />
        )}
      </Card>
      {query.isError && (
        <Typography.Paragraph type="danger" style={{ marginTop: 16 }}>
          加载失败：{query.error instanceof Error ? query.error.message : '未知错误'}
        </Typography.Paragraph>
      )}
    </div>
  );
}
